import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Plus, CheckCircle2, AlertCircle, Upload, Search, ChevronUp, ChevronDown } from 'lucide-react'
import axiosInstance from '../../services/axiosInstance'
import { useAuth } from '../../contexts/AuthContext'
import PageHeader from '../../components/common/PageHeader'
import ProgramModal from '../../components/modals/ProgramModal'
import IntakeModal from '../../components/modals/IntakeModal'
import DocumentRulesModal from '../../components/modals/DocumentRulesModal'
import DeleteModal from '../../components/modals/DeleteModal'
import UniversityModal from '../../components/modals/UniversityModal'
import ConfirmationModal from '../../components/modals/ConfirmationModal'
import AssignRoleModal from '../../components/modals/AssignRoleModal'
import EditAgentTaskModal from '../../components/modals/EditAgentTaskModal'
import './styles.scss'

export default function Settings() {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [successMsg, setSuccessMsg] = useState('')
  
  const [inviteEmail, setInviteEmail] = useState('')
  const [searchLoading, setSearchLoading] = useState(false)
  const [foundUser, setFoundUser] = useState(null)
  const [isAssignRoleModalOpen, setIsAssignRoleModalOpen] = useState(false)

  const isSuperAdmin = user?.roles?.includes('SUPER_ADMIN')

  // Modals state
  const [isProgramModalOpen, setIsProgramModalOpen] = useState(false)
  const [isIntakeModalOpen, setIsIntakeModalOpen] = useState(false)
  const [isRulesModalOpen, setIsRulesModalOpen] = useState(false)
  const [isUniversityModalOpen, setIsUniversityModalOpen] = useState(false)
  const [isAgentTaskModalOpen, setIsAgentTaskModalOpen] = useState(false)
  const [programToDeactivate, setProgramToDeactivate] = useState(null)
  const [ruleForAgentTask, setRuleForAgentTask] = useState(null)
  const [memberToRemove, setMemberToRemove] = useState(null)
  const [selectedProgram, setSelectedProgram] = useState(null)
  const [selectedIntake, setSelectedIntake] = useState(null)

  // Data state
  const [university, setUniversity] = useState(null)
  const [programs, setPrograms] = useState([])
  const [intakes, setIntakes] = useState([])
  const [team, setTeam] = useState([])
  const [allRules, setAllRules] = useState([])
  const [ruleToEdit, setRuleToEdit] = useState(null)
  const [ruleToDelete, setRuleToDelete] = useState(null)

  useEffect(() => {
    fetchSettingsData()
  }, [])

  const fetchSettingsData = async () => {
    setLoading(true)
    setError('')
    try {
      const [univRes, progRes, intRes, teamRes, rulesRes] = await Promise.all([
        axiosInstance.get('/api/settings/university'),
        axiosInstance.get('/api/settings/programs'),
        axiosInstance.get('/api/settings/intakes'),
        axiosInstance.get('/api/settings/team'),
        axiosInstance.get('/api/settings/document-rules')
      ])

      setUniversity(univRes.data)
      setPrograms(progRes.data)
      setIntakes(intRes.data)
      setTeam(teamRes.data)
      setAllRules(rulesRes.data)
    } catch (err) {
      console.error(err)
      setError('Failed to load settings data. Please ensure you are an Admin.')
    } finally {
      setLoading(false)
    }
  }

  // --- Handlers for Forms --- //
  const handleUniversityUpdate = async (updatedData) => {
    try {
      const res = await axiosInstance.put('/api/settings/university', updatedData)
      setUniversity(res.data)
      setSuccessMsg('University configuration saved.')
      window.dispatchEvent(new Event('university-updated'))
      setIsUniversityModalOpen(false)
      setTimeout(() => setSuccessMsg(''), 3000)
    } catch (err) {
      setError('Failed to update university settings.')
    }
  }

  const handleSaveProgram = async (programData) => {
    try {
      if (programData.id) {
        const res = await axiosInstance.put(`/api/settings/programs/${programData.id}`, programData)
        setPrograms(programs.map(p => p.id === programData.id ? res.data : p))
        setSuccessMsg('Program updated successfully.')
      } else {
        const res = await axiosInstance.post('/api/settings/programs', programData)
        setPrograms([...programs, res.data])
        setSuccessMsg('Program added successfully.')
      }
      setIsProgramModalOpen(false)
      setTimeout(() => setSuccessMsg(''), 3000)
    } catch (err) {
      setError('Failed to save program.')
    }
  }

  const openProgramModal = (program = null) => {
    setSelectedProgram(program)
    setIsProgramModalOpen(true)
  }

  const openAddRuleModal = (program) => {
    setSelectedProgram(program)
    setRuleToEdit(null)
    setIsRulesModalOpen(true)
  }

  const openEditRuleModal = (program, rule) => {
    setSelectedProgram(program)
    setRuleToEdit(rule)
    setIsRulesModalOpen(true)
  }

  const confirmDeleteRule = (rule) => {
    setRuleToDelete(rule)
  }

  const handleDeleteRule = async () => {
    if (!ruleToDelete) return
    try {
      await axiosInstance.delete(`/api/settings/document-rules/${ruleToDelete.id}`)
      setAllRules(allRules.filter(r => r.id !== ruleToDelete.id))
      setRuleToDelete(null)
      setSuccessMsg('Rule deleted successfully.')
      setTimeout(() => setSuccessMsg(''), 3000)
    } catch (err) {
      setError('Failed to delete rule.')
      setRuleToDelete(null)
    }
  }

  const handleMoveRule = async (programRules, index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === programRules.length - 1) return;

    const currentRule = programRules[index];
    const swapRule = programRules[direction === 'up' ? index - 1 : index + 1];

    let newCurrentOrder = swapRule.displayOrder || 0;
    let newSwapOrder = currentRule.displayOrder || 0;

    // If they share the same order, force a difference so they swap visibly
    if (newCurrentOrder === newSwapOrder) {
      if (direction === 'up') {
        newCurrentOrder -= 1;
        newSwapOrder += 1;
      } else {
        newCurrentOrder += 1;
        newSwapOrder -= 1;
      }
    }

    try {
      // Optimistic UI update
      setAllRules(allRules.map(r => {
        if (r.id === currentRule.id) return { ...r, displayOrder: newCurrentOrder };
        if (r.id === swapRule.id) return { ...r, displayOrder: newSwapOrder };
        return r;
      }));

      await Promise.all([
        axiosInstance.put(`/api/settings/document-rules/${currentRule.id}`, { ...currentRule, displayOrder: newCurrentOrder }),
        axiosInstance.put(`/api/settings/document-rules/${swapRule.id}`, { ...swapRule, displayOrder: newSwapOrder })
      ]);
    } catch (err) {
      setError('Failed to reorder rules.');
      fetchSettingsData(); // Revert to source of truth on error
    }
  }

  const handleSaveAgentTask = async (ruleId, agentTaskId) => {
    try {
      await axiosInstance.put(`/api/settings/document-rules/${ruleId}/agent-task`, { linqraAgentTaskId: agentTaskId })
      setSuccessMsg('Agent Task ID updated successfully.')
      fetchSettingsData() // Refresh list
    } catch (err) {
      setError('Failed to update Agent Task ID.')
    }
  }

  const openAgentTaskModal = (rule) => {
    setRuleForAgentTask(rule)
    setIsAgentTaskModalOpen(true)
  }

  const confirmDeactivateProgram = (program) => {
    setProgramToDeactivate(program)
  }

  const handleDeactivateProgram = async () => {
    if (!programToDeactivate) return
    try {
      await axiosInstance.delete(`/api/settings/programs/${programToDeactivate.id}`)
      setPrograms(programs.map(p => p.id === programToDeactivate.id ? { ...p, active: false } : p))
      setProgramToDeactivate(null)
    } catch (err) {
      setError('Failed to deactivate program.')
    }
  }

  const handleSaveIntake = async (intakeData) => {
    try {
      if (intakeData.id) {
        const res = await axiosInstance.put(`/api/settings/intakes/${intakeData.id}`, intakeData)
        setIntakes(intakes.map(i => i.id === intakeData.id ? res.data : i))
        setSuccessMsg('Intake updated successfully.')
      } else {
        const res = await axiosInstance.post('/api/settings/intakes', intakeData)
        setIntakes([...intakes, res.data])
        setSuccessMsg('Intake added successfully.')
      }
      setIsIntakeModalOpen(false)
      setTimeout(() => setSuccessMsg(''), 3000)
    } catch (err) {
      setError('Failed to save intake.')
    }
  }

  const openIntakeModal = (intake = null) => {
    setSelectedIntake(intake)
    setIsIntakeModalOpen(true)
  }

  const handleAssignProgram = async (userId, programId, assign) => {
    try {
      const member = team.find(m => m.id === userId)
      let updatedIds = new Set(member.managedProgramIds || [])
      
      if (assign) {
        updatedIds.add(programId)
      } else {
        updatedIds.delete(programId)
      }

      await axiosInstance.put(`/api/settings/team/${userId}/programs`, Array.from(updatedIds))
      
      setTeam(team.map(m => m.id === userId ? { ...m, managedProgramIds: Array.from(updatedIds) } : m))
      setSuccessMsg('Program assignments saved.')
      setTimeout(() => setSuccessMsg(''), 2000)
    } catch (err) {
      setError('Failed to update assignments.')
    }
  }

  const handleAddTeamMember = async (data) => {
    // data contains: { email, role, programId }
    await axiosInstance.post('/api/settings/team/add', data)
    await fetchSettingsData()
    setIsAssignRoleModalOpen(false)
  }

  const handleRemoveMember = async () => {
    if (!memberToRemove) return
    try {
      await axiosInstance.delete(`/api/settings/team/${memberToRemove.member.id}/programs/${memberToRemove.programId}`)
      
      setTeam(team.map(m => {
        if (m.id === memberToRemove.member.id) {
          return {
            ...m,
            managedProgramIds: m.managedProgramIds.filter(pid => pid !== memberToRemove.programId)
          }
        }
        return m
      }))
      setSuccessMsg(`Successfully removed ${memberToRemove.member.fullName} from the program.`)
      setTimeout(() => setSuccessMsg(''), 4000)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove team member.')
      setTimeout(() => setError(''), 4000)
    } finally {
      setMemberToRemove(null)
    }
  }

  // --- Render Sections --- //
  const renderGeneral = () => (
    <div className="settings-card glass-panel">
      <div className="card-header flex-between">
        <div>
          <h3>General Configuration</h3>
          <p className="text-muted">Your organization's core details.</p>
        </div>
        {isSuperAdmin && (
          <button className="btn btn-outline btn-sm" onClick={() => setIsUniversityModalOpen(true)}>
            Edit Organization
          </button>
        )}
      </div>
      
      <div className="university-card">
        <div className="university-logo-box">
          {university?.logoUrl ? (
            <img src={university.logoUrl} alt="University Logo" />
          ) : (
            <span className="placeholder-icon">🏢</span>
          )}
        </div>
        
        <div className="university-details">
          <h4>{university?.name || 'Organization Name'}</h4>
          <div className="detail-row">
            <div>
              <strong>Slug:</strong> {university?.slug || 'N/A'}
            </div>
            <div>
              <strong>Website:</strong> {university?.website || 'N/A'}
            </div>
          </div>
          <div className="detail-row">
            <div>
              <strong>Location:</strong> {[university?.city, university?.state, university?.country].filter(Boolean).join(', ') || 'N/A'}
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderPrograms = () => (
    <div className="settings-card glass-panel full-width">
      <div className="card-header flex-between">
        <div>
          <h3>Academic Programs & Rules</h3>
          <p className="text-muted">Manage the degrees your university offers and their required documents.</p>
        </div>
        <button className="btn btn-primary" onClick={() => openProgramModal()}>
          <Plus size={16} /> Add Program
        </button>
      </div>

      <div className="programs-container">
        {programs.map(p => {
          const programRules = allRules.filter(r => r.programId === p.id && r.active)
                                       .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
          
          return (
            <div key={p.id} className="program-item">
              {/* Program Header */}
              <div className="program-item-header">
                <div>
                  <h4>{p.name}</h4>
                  <div className="program-meta">
                    <span><strong>Degree Level:</strong> {p.degreeLevel || 'N/A'}</span>
                    <span>
                      {p.active ? <span className="badge badge--success" style={{fontSize: '0.7rem'}}>Active</span> : <span className="badge badge--neutral" style={{fontSize: '0.7rem'}}>Inactive</span>}
                      {p.locked && <span className="badge" style={{ marginLeft: '0.5rem', background: '#fee2e2', color: '#dc2626', fontSize: '0.7rem' }}>Locked</span>}
                    </span>
                  </div>
                </div>
                
                <div className="program-actions">
                  {(!p.locked || isSuperAdmin) ? (
                    <>
                      <button className="btn btn-primary btn-sm" onClick={() => openAddRuleModal(p)}>
                        <Plus size={14} style={{ marginRight: '4px' }} /> Add Rule
                      </button>
                      <button className="btn btn-outline btn-sm" onClick={() => openProgramModal(p)}>
                        Edit Details
                      </button>
                      {p.active && (
                        <button className="btn btn-outline btn-sm" onClick={() => confirmDeactivateProgram(p)} style={{ color: '#dc2626', borderColor: '#fca5a5' }}>
                          Deactivate
                        </button>
                      )}
                    </>
                  ) : (
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>Locked by Super Admin</span>
                  )}
                </div>
              </div>
              
              {/* Document Rules Inline Table */}
              <div className="rules-container">
                <h5>
                  Required Documents Checklist
                  <span className="rules-count">{programRules.length} Rules Configured</span>
                </h5>
                
                {programRules.length > 0 ? (
                  <div className="data-table-container rules-table-wrapper">
                    <table className="data-table" style={{ margin: 0 }}>
                      <thead>
                        <tr>
                          <th style={{ width: '80px', textAlign: 'center' }}>Order</th>
                          <th>Document Type</th>
                          <th>Description</th>
                          <th>Requirement</th>
                          <th className="text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {programRules.map((rule, idx) => (
                          <tr key={rule.id}>
                            <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                              <div className="order-controls">
                                <div className="order-arrows">
                                  <button 
                                    type="button"
                                    onClick={() => handleMoveRule(programRules, idx, 'up')}
                                    disabled={idx === 0}
                                  >
                                    <ChevronUp size={16} />
                                  </button>
                                  <button 
                                    type="button"
                                    onClick={() => handleMoveRule(programRules, idx, 'down')}
                                    disabled={idx === programRules.length - 1}
                                  >
                                    <ChevronDown size={16} />
                                  </button>
                                </div>
                                <span className="order-number">{rule.displayOrder || 0}</span>
                              </div>
                            </td>
                            <td className="font-medium">{rule.documentType.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</td>
                            <td className="text-muted">{rule.description}</td>
                            <td>
                              {rule.required ? 
                                <span className="badge badge--primary" style={{fontSize: '0.7rem'}}>Required</span> : 
                                <span className="badge" style={{ background: '#fef3c7', color: '#b45309', fontSize: '0.7rem' }}>Optional</span>
                              }
                            </td>
                            <td className="text-right">
                              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                {isSuperAdmin && (
                                  <button 
                                    type="button" 
                                    className={`btn btn-sm ai-task-btn ${rule.linqraAgentTaskId ? 'connected' : 'unconnected'}`}
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openAgentTaskModal(rule); }}
                                  >
                                    {rule.linqraAgentTaskId ? 'AI Task ✓' : 'Link AI +'}
                                  </button>
                                )}
                                <button className="btn btn-outline btn-sm" onClick={() => openEditRuleModal(p, rule)}>
                                  Edit
                                </button>
                                <button className="btn btn-outline btn-sm" onClick={() => confirmDeleteRule(rule)} style={{ color: '#dc2626', borderColor: '#fca5a5' }}>
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="no-rules-state">
                    No document rules defined. Click "Add Rule" to configure the checklist sequence.
                  </div>
                )}
              </div>
            </div>
          )
        })}
        
        {programs.length === 0 && (
          <div className="empty-state" style={{ padding: '3rem' }}>No programs configured.</div>
        )}
      </div>
    </div>
  )

  const renderIntakes = () => (
    <div className="settings-card glass-panel full-width">
      <div className="card-header flex-between">
        <div>
          <h3>Intakes & Terms</h3>
          <p className="text-muted">Configure active academic terms.</p>
        </div>
        <button className="btn btn-primary" onClick={() => openIntakeModal()}>
          <Plus size={16} /> Add Intake
        </button>
      </div>

      <div className="data-table-container">
        <table className="data-table">
          <thead>
              <tr>
              <th>Intake Name</th>
              <th>Term & Year</th>
              <th>Start Date</th>
              <th>Deadline</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {intakes.map(i => (
              <tr key={i.id}>
                <td className="font-medium">{i.name}</td>
                <td>{i.term} {i.year}</td>
                <td>{i.startDate ? new Date(i.startDate).toLocaleDateString() : 'N/A'}</td>
                <td>{i.applicationDeadline ? new Date(i.applicationDeadline).toLocaleDateString() : 'N/A'}</td>
                <td>
                  {i.active ? <span className="badge badge--success">Active</span> : <span className="badge badge--neutral">Inactive</span>}
                </td>
                <td className="text-right">
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button className="btn btn-outline btn-sm" onClick={() => openIntakeModal(i)}>
                      Edit
                    </button>
                    {/* Add Deactivate if needed */}
                  </div>
                </td>
              </tr>
            ))}
            {intakes.length === 0 && <tr><td colSpan="6" className="empty-state">No intakes configured.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )

  const renderTeam = () => (
    <div className="settings-card glass-panel full-width">
      <div className="card-header flex-between" style={{ alignItems: 'flex-start' }}>
        <div>
          <h3>Team Management & Roles</h3>
          <p className="text-muted">Assign admins to specific programs so they can handle reviews.</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
          <form onSubmit={async (e) => {
            e.preventDefault()
            if (inviteEmail.trim()) {
              setSearchLoading(true)
              try {
                const res = await axiosInstance.get(`/api/settings/team/search?email=${encodeURIComponent(inviteEmail.trim())}`)
                setFoundUser(res.data)
              } catch (err) {
                setFoundUser(null) // User not found, but we still open modal to invite them
              } finally {
                setSearchLoading(false)
                setIsAssignRoleModalOpen(true)
              }
            }
          }} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input 
              type="email" 
              placeholder="User's email address..." 
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', width: '220px' }}
              required
            />
            <button type="submit" className="btn btn-primary btn-sm" disabled={!inviteEmail.trim() || searchLoading}>
              <Search size={16} style={{ marginRight: '6px' }} />
              {searchLoading ? 'Searching...' : 'Search & Assign Role'}
            </button>
          </form>
        </div>
      </div>

      <div className="team-grid">
        {programs.filter(p => p.active).map(prog => {
          const programMembers = team.filter(m => m.managedProgramIds?.includes(prog.id));
          
          return (
            <div key={prog.id} className="team-card" style={{ padding: '20px' }}>
              <div style={{ marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: 0, color: '#1e293b', fontSize: '1.1rem' }}>{prog.name}</h4>
                <p className="text-muted" style={{ margin: '4px 0 0 0', fontSize: '0.9rem' }}>Admins assigned to review applications</p>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {programMembers.map(member => (
                  <div key={member.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '12px', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <div className="avatar" style={{ width: '36px', height: '36px', fontSize: '16px', marginTop: '2px' }}>
                        {member.fullName.charAt(0)}
                      </div>
                      <div>
                        <Link to={`/profile/${member.id}`} style={{ textDecoration: 'none' }}>
                          <h5 style={{ margin: 0, color: '#2563eb', fontSize: '0.95rem' }}>{member.fullName}</h5>
                        </Link>
                        <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem' }}>{member.email}</p>
                        <div style={{ display: 'flex', gap: '4px', marginTop: '6px', flexWrap: 'wrap' }}>
                          {member.roles?.filter(r => r !== 'USER').map(r => (
                            <span key={r} className="badge badge--primary" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>{r}</span>
                          ))}
                          {(!member.roles || member.roles.filter(r => r !== 'USER').length === 0) && (
                            <span className="badge badge--neutral" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>STAFF</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {member.id !== user?.id && (
                      <button 
                        className="btn btn-outline btn-sm" 
                        onClick={() => setMemberToRemove({ member, programId: prog.id })}
                        style={{ color: '#dc2626', borderColor: '#fca5a5', padding: '4px 12px', height: 'auto', minHeight: '32px' }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                
                {programMembers.length === 0 && (
                  <div className="text-muted" style={{ textAlign: 'center', padding: '16px 0', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                    No admins assigned to this program.
                  </div>
                )}
              </div>
            </div>
          )
        })}
        {programs.filter(p => p.active).length === 0 && (
           <div className="empty-state">No active programs available.</div>
        )}
      </div>
    </div>
  )

  const renderRoleDescriptions = () => (
    <div className="settings-card glass-panel full-width" style={{ marginTop: '24px' }}>
      <div className="card-header">
        <h3>Role Definitions & Scope</h3>
        <p className="text-muted">Understanding what each role can access</p>
      </div>
      <div className="card-content">
        <div className="role-list">
          <div className="role-item admin">
            <h4>
              <span className="badge badge--primary">ADMIN</span>
            </h4>
            <p>
              University-level administrator. Can manage programs, intakes, document rules, and invite other staff members for their specific university.
            </p>
          </div>

          <div className="role-item staff">
            <h4>
              <span className="badge badge--primary">STAFF</span>
            </h4>
            <p>
              Admissions reviewer. Can only view and process applications for the specific Programs they have been assigned to. Cannot change university settings.
            </p>
          </div>

          <div className="role-item agent">
            <h4>
              <span className="badge badge--primary">AGENT</span>
            </h4>
            <p>
              Recruitment partner. Can submit applications on behalf of students and track the status of only the applications they submitted.
            </p>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="settings-page">
      <PageHeader 
        title="Settings & Administration" 
        subtitle="Configure your university, programs, and team roles"
      />

      {error && <div className="alert alert-danger"><AlertCircle size={18}/> {error}</div>}
      {successMsg && <div className="alert alert-success"><CheckCircle2 size={18}/> {successMsg}</div>}

      {loading ? (
        <div className="settings-loading">Loading configuration...</div>
      ) : (
        <div className="settings-grid">
          {/* Top row: General config */}
          <div className="grid-row-top">
            {renderGeneral()}
          </div>
          
          {/* Middle row: Programs and Intakes side-by-side or stacked depending on size */}
          {renderPrograms()}
          {renderIntakes()}

          {/* Bottom row: Team */}
          {renderTeam()}
          {renderRoleDescriptions()}
        </div>
      )}

      <ProgramModal 
        isOpen={isProgramModalOpen} 
        onClose={() => setIsProgramModalOpen(false)} 
        onSave={handleSaveProgram}
        initialData={selectedProgram}
        isSuperAdmin={isSuperAdmin}
      />
      
      <IntakeModal 
        isOpen={isIntakeModalOpen} 
        onClose={() => setIsIntakeModalOpen(false)} 
        onSave={handleSaveIntake}
        initialData={selectedIntake}
      />

      {(() => {
        const programRules = selectedProgram ? allRules.filter(r => r.programId === selectedProgram.id && r.active).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)) : []
        const maxOrder = programRules.length > 0 ? programRules[programRules.length - 1].displayOrder || 0 : 0
        return (
          <DocumentRulesModal 
            isOpen={isRulesModalOpen}
            onClose={() => {
              setIsRulesModalOpen(false)
              setRuleToEdit(null)
              fetchSettingsData() // Refresh inline rules when modal closes
            }}
            program={selectedProgram}
            ruleToEdit={ruleToEdit}
            nextDisplayOrder={maxOrder + 1}
          />
        )
      })()}

      <DeleteModal 
        isOpen={!!ruleToDelete}
        onClose={() => setRuleToDelete(null)}
        onConfirm={handleDeleteRule}
        title="Delete Document Rule"
        message={`Are you sure you want to delete the rule for "${ruleToDelete?.description}"? Applications that already have this item generated in their checklist will retain it, but future applications will not require it.`}
        confirmText="Delete Rule"
      />

      <UniversityModal
        isOpen={isUniversityModalOpen}
        onClose={() => setIsUniversityModalOpen(false)}
        onSave={handleUniversityUpdate}
        initialData={university}
      />

      <DeleteModal 
        isOpen={!!programToDeactivate}
        onClose={() => setProgramToDeactivate(null)}
        onConfirm={handleDeactivateProgram}
        title="Deactivate Program"
        message={`Are you sure you want to deactivate the "${programToDeactivate?.name}" program? It will no longer be visible to new applicants.`}
        confirmText="Deactivate"
      />

      <ConfirmationModal
        isOpen={!!memberToRemove}
        onClose={() => setMemberToRemove(null)}
        onConfirm={handleRemoveMember}
        title="Remove Team Member"
        message={`Are you sure you want to remove ${memberToRemove?.member?.fullName} from this program?`}
        confirmText="Remove Member"
        cancelText="Cancel"
        type="danger"
      />
      {/* Assign Role Modal */}
      <AssignRoleModal
        isOpen={isAssignRoleModalOpen}
        onClose={() => setIsAssignRoleModalOpen(false)}
        onSave={handleAddTeamMember}
        programs={programs}
        initialEmail={inviteEmail}
        foundUser={foundUser}
      />

      <EditAgentTaskModal
        isOpen={isAgentTaskModalOpen}
        onClose={() => setIsAgentTaskModalOpen(false)}
        onSave={handleSaveAgentTask}
        rule={ruleForAgentTask}
      />
    </div>
  )
}
